import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { courseApi } from '../api/courseApi';
import { CourseSearchItem } from '../types';
import { useDeviceCourses } from '../context/DeviceCoursesContext';
import { useProfile } from '../context/ProfileContext';
import { US_STATES } from '../constants/usStates';

/**
 * Search the OpenGolf catalog by course name and save courses to this device.
 * Results are a fuzzy name match scoped to the golfer's home state (set during
 * onboarding). Saved courses are kept locally (see DeviceCoursesContext) and can
 * be selected to play from the Upload page.
 */
const CourseSearchPanel: React.FC = () => {
  const { savedCourses, saveCourse, removeCourse, selectedCourseId } = useDeviceCourses();
  const { profile } = useProfile();
  const [query, setQuery] = useState('');
  const [searchState, setSearchState] = useState(profile.state);
  const [results, setResults] = useState<CourseSearchItem[]>([]);
  const [searching, setSearching] = useState(false);
  const toast = useRef<Toast>(null);

  // Default the state filter to the golfer's home state once the profile loads,
  // without clobbering a selection the golfer has already made.
  useEffect(() => {
    if (profile.state) setSearchState((prev) => prev || profile.state);
  }, [profile.state]);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      setResults(await courseApi.search(query.trim(), searchState || undefined));
    } catch {
      toast.current?.show({ severity: 'error', summary: 'Search failed', detail: 'Could not search for courses.', life: 4000 });
    } finally {
      setSearching(false);
    }
  };

  const handleSave = (course: CourseSearchItem) => {
    saveCourse(course);
    toast.current?.show({ severity: 'success', summary: 'Saved to device', detail: course.name, life: 2500 });
  };

  const savedIds = new Set(savedCourses.map((c) => c.id));

  return (
    <section className="card-surface course-panel">
      <Toast ref={toast} />
      <h2>⛳ My Courses</h2>

      <div className="course-search">
        <div className="search-field">
          <label htmlFor="profile-course-query">Course name</label>
          <InputText
            id="profile-course-query"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. Pebble Beach"
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </div>
        <div className="search-field state-field">
          <label htmlFor="profile-course-state">State</label>
          <Dropdown
            inputId="profile-course-state"
            value={searchState}
            onChange={(e) => setSearchState(e.value ?? '')}
            options={US_STATES}
            optionLabel="name"
            optionValue="code"
            placeholder="All states"
            filter
          />
        </div>
        <Button
          label="Search"
          icon="pi pi-search"
          className="search-btn"
          onClick={handleSearch}
          loading={searching}
          disabled={!query.trim()}
        />
      </div>

      {results.length > 0 && (
        <div className="table-scroll">
          <table className="course-table">
            <thead>
              <tr>
                <th>Save course</th>
                <th>Name</th>
                <th>Location</th>
                <th>Par</th>
                <th>Holes</th>
              </tr>
            </thead>
            <tbody>
              {results.map((c) => (
                <tr key={c.id}>
                  <td>
                    {savedIds.has(c.id) ? (
                      <span className="saved-tag">Saved</span>
                    ) : (
                      <Button label="Save course" icon="pi pi-download" size="small" onClick={() => handleSave(c)} />
                    )}
                  </td>
                  <td>{c.name}</td>
                  <td>{[c.city, c.state].filter(Boolean).join(', ')}</td>
                  <td>{c.par}</td>
                  <td>{c.holes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h3 className="saved-courses-title">Saved on this device</h3>
      {savedCourses.length === 0 ? (
        <p className="admin-sub">No courses saved yet.</p>
      ) : (
        <ul className="saved-course-list">
          {savedCourses.map((c) => (
            <li key={c.id} className={c.id === selectedCourseId ? 'selected' : ''}>
              <div className="saved-course-info">
                <span className="saved-course-name">{c.name}</span>
                <span className="saved-course-loc">{[c.city, c.state].filter(Boolean).join(', ')}</span>
              </div>
              {c.id === selectedCourseId && <span className="playing-tag"><i className="pi pi-flag-fill" /> Selected</span>}
              <Button
                icon="pi pi-trash"
                size="small"
                rounded
                text
                severity="danger"
                aria-label={`Remove ${c.name}`}
                onClick={() => removeCourse(c.id)}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default CourseSearchPanel;
